import { useEffect, useState } from "react";
import { PageMeta } from "@/components/PageMeta";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { routes } from "@/lib/routes";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ArrowRight, Sparkles, Users, Award, Play, BookOpen, MapPin } from "lucide-react";
import mateoLogo from "@/assets/mateo-logo.png";
import { NotificationBell } from "@/components/NotificationBell";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { PublicChatWidget } from "@/components/chat/PublicChatWidget";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { captureAffiliateCode, getOrCreateVisitorId } from "@/lib/affiliateTracking";
import { LivePlatformStats } from "@/components/LivePlatformStats";
import { HomepageSearch } from "@/components/HomepageSearch";

const Index = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showVideoDialog, setShowVideoDialog] = useState(false);
  const [showCoachVideoDialog, setShowCoachVideoDialog] = useState(false);
  const [showAIVideoDialog, setShowAIVideoDialog] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [coachVideoProgress, setCoachVideoProgress] = useState(0);
  const [aiVideoProgress, setAIVideoProgress] = useState(0);

  // Capture affiliate code from URL and record server-side click
  useEffect(() => {
    const code = captureAffiliateCode();
    if (code) {
      const visitorId = getOrCreateVisitorId();
      // Record anonymous click event server-side
      (async () => {
        try {
          const { data: affPartner } = await supabase
            .from('affiliate_partners')
            .select('id')
            .eq('affiliate_code', code)
            .eq('status', 'approved')
            .maybeSingle();

          if (affPartner) {
            await supabase.from('affiliate_referrals').insert({
              affiliate_id: affPartner.id,
              visitor_id: visitorId,
              landing_url: window.location.href,
              status: 'clicked',
            } as any);
          }
        } catch (err) {
          console.error('Affiliate click tracking error:', err);
        }
      })();
    }
  }, []);

  const closeSkillSeekersVideo = () => {
    setVideoProgress(0);
    setShowVideoDialog(false);
    navigate(routes.allExperiences());
  };

  const closeCoachVideo = () => {
    setCoachVideoProgress(0);
    setShowCoachVideoDialog(false);
    navigate(routes.allExperiences());
  };

  const closeAIVideo = () => {
    setAIVideoProgress(0);
    setShowAIVideoDialog(false);
    navigate(routes.allExperiences());
  };

  useEffect(() => {
    // Set up auth listener BEFORE checking session to avoid race conditions
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setIsAuthenticated(!!session);
      }
    );

    // Check initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsAuthenticated(!!session);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-primary/5 to-accent/10">
      <PageMeta
        title="Mateo – Book Local Coaches & Learn New Skills Near You"
        description="Mateo is the skills marketplace that connects you with expert coaches nearby. Book in-person lessons in fitness, music, cooking, arts, languages and more. Try it free."
        canonicalPath="/"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Organization",
                "name": "Mateo",
                "url": "https://www.gomateo.io",
                "logo": "https://www.gomateo.io/favicon.png",
                "sameAs": ["https://www.facebook.com/gomateo", "https://twitter.com/gomateo"],
                "description": "Mateo is a skills marketplace connecting learners with local coaches for in-person lessons in fitness, music, cooking, arts, languages and more."
              },
              {
                "@type": "WebSite",
                "name": "Mateo",
                "url": "https://www.gomateo.io",
                "potentialAction": {
                  "@type": "SearchAction",
                  "target": "https://www.gomateo.io/browse?search={search_term_string}",
                  "query-input": "required name=search_term_string"
                }
              },
              {
                "@type": "LocalBusiness",
                "name": "Mateo",
                "url": "https://www.gomateo.io",
                "description": "On-demand marketplace for real-world skills and spontaneous experiences. Find local coaches for in-person lessons.",
                "areaServed": [
                  { "@type": "City", "name": "Toronto" },
                  { "@type": "City", "name": "Calgary" },
                  { "@type": "City", "name": "Amsterdam" },
                  { "@type": "City", "name": "Boston" }
                ],
                "priceRange": "$$",
                "aggregateRating": {
                  "@type": "AggregateRating",
                  "ratingValue": "4.9",
                  "reviewCount": "50"
                }
              }
            ]
          })
        }}
      />
      <header className="container mx-auto px-4 py-4 md:py-6">
        <div className="flex items-center justify-between gap-2">
          <img 
            src={mateoLogo} 
            alt="Mateo – Local Skills Marketplace" 
            className="h-20 sm:h-32 md:h-40 lg:h-56 w-auto shrink-0" 
            fetchPriority="high"
          />
          <span className="text-sm sm:text-xl md:text-2xl font-bold text-primary tracking-wide hidden sm:block">
            {t('home.tagline')}
          </span>
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <LanguageSwitcher />
            {isAuthenticated && <NotificationBell />}
            {isAuthenticated ? (
              <Button variant="outline" size="sm" className="text-xs sm:text-sm" onClick={() => navigate(routes.dashboard())}>
                {t('nav.dashboard')}
              </Button>
            ) : (
              <Button variant="outline" size="sm" className="text-xs sm:text-sm" onClick={() => navigate(routes.auth())}>
                {t('nav.signIn')}
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 pt-4 pb-20">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          {/* Post-launch Get Started CTA — replaces the old countdown banner */}
          {!isAuthenticated && (
            <div className="bg-gradient-to-r from-primary/10 via-accent/10 to-orange/10 border border-primary/20 rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              <div className="flex items-center gap-2 text-sm sm:text-base font-semibold text-foreground">
                <Sparkles className="h-4 w-4 text-primary" aria-hidden="true" />
                <span>Ready to get started?</span>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Button
                  size="sm"
                  onClick={() => navigate(`${routes.auth()}?signup=1&role=learner`)}
                  className="gap-1.5"
                >
                  I'm here to learn
                  <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate(routes.becomeCoach())}
                  className="gap-1.5 border-orange/40 text-orange hover:bg-orange/10 hover:text-orange"
                >
                  I want to coach
                  <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                </Button>
              </div>
            </div>
          )}

          <h1 className="text-4xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight px-2">
            <span className="text-orange">{t('home.heroLine1')}</span><br />
            <span className="bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent">
              {t('home.heroLine2')}
            </span>
          </h1>
          
          <p className="text-lg sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto px-4 leading-relaxed">
            {t('home.description')}
          </p>

          {/* Search bar — direct path to browse */}
          <HomepageSearch />

          {/* Live social proof stats */}
          <LivePlatformStats />

          {/* Primary CTA: Coach recruitment (supply priority) */}
          <button
            type="button"
            aria-label="Become an Experience Provider — start in 10 minutes"
            className="relative max-w-xl mx-auto w-full p-6 sm:p-8 rounded-2xl bg-gradient-to-br from-orange/10 via-card to-primary/5 shadow-card hover:shadow-card-hover transition-smooth cursor-pointer border border-orange/20 hover:border-orange/40 group overflow-hidden text-left"
            onClick={() => navigate(routes.becomeCoach())}
          >
            <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-orange/15 text-orange text-xs font-semibold">
              Now Recruiting
            </div>
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-2xl bg-orange/10 flex items-center justify-center shrink-0 group-hover:bg-orange/20 transition-colors">
                <Award className="h-7 w-7 text-orange" aria-hidden="true" />
              </div>
              <div className="text-left">
                <h2 className="text-xl font-bold mb-1 text-foreground">{t('home.becomeProvider', 'Become an Experience Provider')}</h2>
                <p className="text-sm text-muted-foreground mb-3">Turn your talent into income. AI builds your profile, finds your students, and handles admin — you just teach.</p>
                <div className="flex flex-wrap gap-3 text-xs">
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-background border border-border text-foreground font-medium">
                    <Sparkles className="h-3 w-3 text-orange" aria-hidden="true" /> No upfront costs
                  </span>
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-background border border-border text-foreground font-medium">
                    <MapPin className="h-3 w-3 text-primary" aria-hidden="true" /> Set your own schedule
                  </span>
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-background border border-border text-foreground font-medium">
                    <Users className="h-3 w-3 text-primary" aria-hidden="true" /> 450+ active learners
                  </span>
                </div>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-end gap-2 text-sm font-semibold text-orange group-hover:gap-3 transition-all">
              Start in 10 minutes <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </div>
          </button>

          {/* Secondary: Discover Experiences */}
          <button
            type="button"
            aria-label="Discover local experiences and coaches"
            className="max-w-xl mx-auto w-full p-5 rounded-2xl bg-card shadow-card hover:shadow-card-hover transition-smooth cursor-pointer border border-border hover:border-primary/30 group text-left"
            onClick={() => {
              const el = document.getElementById('learning-experiences');
              el?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-teal/10 flex items-center justify-center shrink-0">
                <MapPin className="h-5 w-5 text-teal" aria-hidden="true" />
              </div>
              <div className="text-left flex-1">
                <h2 className="text-base font-semibold">{t('home.discoverExperiences', 'Discover Experiences')}</h2>
                <p className="text-xs text-muted-foreground">{t('home.discoverExperiencesDesc', 'Find local coaches and learn something new today')}</p>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" aria-hidden="true" />
            </div>
          </button>

          <Separator className="max-w-md mx-auto my-2" />

          {/* How It Works 1-2-3 */}
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-8" id="how-it-works">
            {t('home.thisIsMateo', 'This is Mateo')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-4 px-2">
            {[
              { step: '1', icon: MapPin, title: t('home.howItWorksStep1Title'), desc: t('home.howItWorksStep1Desc') },
              { step: '2', icon: Sparkles, title: t('home.howItWorksStep2Title'), desc: t('home.howItWorksStep2Desc') },
              { step: '3', icon: Users, title: t('home.howItWorksStep3Title'), desc: t('home.howItWorksStep3Desc') },
            ].map((item) => (
              <div key={item.step} className="text-center space-y-3">
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                  <item.icon className="h-7 w-7 text-primary" />
                </div>
                <div className="text-3xl font-bold text-primary/20">{item.step}</div>
                <h3 className="text-lg font-semibold text-foreground">{item.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 pt-10 sm:pt-16 px-2 sm:px-0">
            <button 
              type="button"
              aria-label="Watch how Mateo works"
              className="p-6 rounded-2xl bg-card shadow-card hover:shadow-card-hover transition-smooth cursor-pointer relative group text-left"
              onClick={() => setShowVideoDialog(true)}
            >
              <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-primary/90 flex items-center justify-center group-hover:bg-primary transition-colors">
                <Play className="h-5 w-5 text-primary-foreground fill-current" aria-hidden="true" />
              </div>
              <div className="w-12 h-12 rounded-full bg-blue/10 flex items-center justify-center mx-auto mb-4">
                <Award className="h-6 w-6 text-blue" aria-hidden="true" />
              </div>
              <h3 className="text-xl sm:text-lg font-semibold mb-2">{t('home.howItWorks')}</h3>
              <p className="text-base sm:text-sm text-muted-foreground leading-relaxed">
                {t('home.dontJustWatch')}
              </p>
            </button>

            <button 
              type="button"
              aria-label="Watch about Mateo coaches"
              className="p-6 rounded-2xl bg-card shadow-card hover:shadow-card-hover transition-smooth cursor-pointer relative group text-left"
              onClick={() => setShowCoachVideoDialog(true)}
            >
              <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-primary/90 flex items-center justify-center group-hover:bg-primary transition-colors">
                <Play className="h-5 w-5 text-primary-foreground fill-current" aria-hidden="true" />
              </div>
              <div className="w-12 h-12 rounded-full bg-teal/10 flex items-center justify-center mx-auto mb-4">
                <Users className="h-6 w-6 text-teal" aria-hidden="true" />
              </div>
              <h3 className="text-xl sm:text-lg font-semibold mb-2">{t('home.mateoCoaches')}</h3>
              <p className="text-base sm:text-sm text-muted-foreground leading-relaxed">
                {t('home.mateoCoachesDesc')}
              </p>
            </button>

            <button 
              type="button"
              aria-label="Watch about AI matching"
              className="p-6 rounded-2xl bg-card shadow-card hover:shadow-card-hover transition-smooth cursor-pointer relative group text-left"
              onClick={() => setShowAIVideoDialog(true)}
            >
              <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-primary/90 flex items-center justify-center group-hover:bg-primary transition-colors">
                <Play className="h-5 w-5 text-primary-foreground fill-current" aria-hidden="true" />
              </div>
              <div className="w-12 h-12 rounded-full bg-orange/10 flex items-center justify-center mx-auto mb-4">
                <Sparkles className="h-6 w-6 text-orange" aria-hidden="true" />
              </div>
              <h3 className="text-xl sm:text-lg font-semibold mb-2">{t('home.aiMatching')}</h3>
              <p className="text-base sm:text-sm text-muted-foreground leading-relaxed">
                {t('home.aiMatchingDesc')}
              </p>
            </button>
          </div>

          <div id="learning-experiences" className="flex flex-col gap-3 items-center pt-6">
            <p className="text-base sm:text-sm text-muted-foreground font-medium">
              {t('home.exploreNoSignup')}
            </p>
            <div className="flex flex-col gap-3 w-full px-4 sm:px-0 sm:flex-row sm:flex-wrap sm:gap-4 sm:justify-center sm:w-auto">
              <Button size="lg" variant="outline" className="text-base sm:text-lg px-6 sm:px-8 w-full sm:w-auto" onClick={() => navigate(routes.allExperiences())}>
                <BookOpen className="mr-2 h-5 w-5" />
                {t('home.seeAllExperiences')}
              </Button>
              <Button size="lg" variant="outline" className="text-base sm:text-lg px-6 sm:px-8 w-full sm:w-auto" onClick={() => navigate(routes.browseExperiences() + '&sort=near_you')}>
                <MapPin className="mr-2 h-5 w-5" />
                {t('home.seeNearMe')}
              </Button>
              <Button size="lg" variant="outline" className="text-base sm:text-lg px-6 sm:px-8 w-full sm:w-auto" onClick={() => navigate(routes.browseCoaches() + '&sort=near_you')}>
                <Users className="mr-2 h-5 w-5" />
                {t('home.seeCoachesNearMe')}
              </Button>
            </div>
          </div>

          <div id="become-coach-section" className="flex flex-col items-center gap-3 pt-8">
            <div className="flex items-center gap-2">
              <Separator className="w-12" />
              <span className="text-sm text-foreground/60 sm:text-muted-foreground">{t('home.or')}</span>
              <Separator className="w-12" />
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto px-4 sm:px-0">
              <Button size="lg" variant="outline" className="text-base sm:text-lg px-6 sm:px-8 w-full sm:w-auto" onClick={() => navigate(routes.becomeCoach())}>
                <Users className="mr-2 h-5 w-5" />
                {t('home.becomeCoach')}
              </Button>
              <Button size="lg" className="text-base sm:text-lg px-6 sm:px-8 w-full sm:w-auto" onClick={() => navigate(routes.allExperiences())}>
                {t('home.experiencesNow', 'Experiences Now')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </main>

      <Dialog open={showVideoDialog} onOpenChange={(open) => {
        if (!open) {
          closeSkillSeekersVideo();
          return;
        }
        setShowVideoDialog(open);
      }}>
        <DialogContent className="max-w-[95vw] sm:max-w-4xl p-4 sm:p-6" onPointerDownOutside={(e) => { e.preventDefault(); closeSkillSeekersVideo(); }}>
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle>{t('home.videoTitleSkill')}</DialogTitle>
          </DialogHeader>
          <div className="aspect-video w-full">
            {showVideoDialog && (
              <video 
                className="w-full h-full rounded-lg" 
                controls 
                autoPlay
                muted
                playsInline
                onTimeUpdate={(e) => {
                  const video = e.currentTarget;
                  const progress = (video.currentTime / video.duration) * 100;
                  setVideoProgress(progress);
                }}
              >
                <source src="/videos/matheo-intro-new.mp4" type="video/mp4" />
              </video>
            )}
          </div>
          <div className="mt-4">
            <Progress value={videoProgress} className="h-2" />
            <p className="text-sm text-muted-foreground mt-2 text-center">
              {t('home.watched', { percent: Math.round(videoProgress) })}
            </p>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showCoachVideoDialog} onOpenChange={(open) => {
        if (!open) {
          closeCoachVideo();
          return;
        }
        setShowCoachVideoDialog(open);
      }}>
        <DialogContent className="max-w-[95vw] sm:max-w-4xl p-4 sm:p-6" onPointerDownOutside={(e) => { e.preventDefault(); closeCoachVideo(); }}>
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle>{t('home.videoTitleCoach')}</DialogTitle>
          </DialogHeader>
          <div className="aspect-video w-full">
            {showCoachVideoDialog && (
              <video 
                className="w-full h-full rounded-lg" 
                controls 
                autoPlay
                muted
                playsInline
                onTimeUpdate={(e) => {
                  const video = e.currentTarget;
                  const progress = (video.currentTime / video.duration) * 100;
                  setCoachVideoProgress(progress);
                }}
              >
                <source src="/videos/coach-explainer-new.mp4" type="video/mp4" />
              </video>
            )}
          </div>
          <div className="mt-4">
            <Progress value={coachVideoProgress} className="h-2" />
            <p className="text-sm text-muted-foreground mt-2 text-center">
              {t('home.watched', { percent: Math.round(coachVideoProgress) })}
            </p>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showAIVideoDialog} onOpenChange={(open) => {
        if (!open) {
          closeAIVideo();
          return;
        }
        setShowAIVideoDialog(open);
      }}>
        <DialogContent className="max-w-[95vw] sm:max-w-4xl p-4 sm:p-6" onPointerDownOutside={(e) => { e.preventDefault(); closeAIVideo(); }}>
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle>{t('home.videoTitleAI')}</DialogTitle>
          </DialogHeader>
          <div className="aspect-video w-full">
            {showAIVideoDialog && (
              <video 
                className="w-full h-full rounded-lg" 
                controls 
                autoPlay
                muted
                playsInline
                onTimeUpdate={(e) => {
                  const video = e.currentTarget;
                  const progress = (video.currentTime / video.duration) * 100;
                  setAIVideoProgress(progress);
                }}
              >
                <source src="/videos/ai-matching-explainer-new.mp4" type="video/mp4" />
              </video>
            )}
          </div>
          <div className="mt-4">
            <Progress value={aiVideoProgress} className="h-2" />
            <p className="text-sm text-muted-foreground mt-2 text-center">
              {t('home.watched', { percent: Math.round(aiVideoProgress) })}
            </p>
          </div>
        </DialogContent>
      </Dialog>

      <PublicChatWidget />
    </div>
  );
};

export default Index;
